import type { CampaignGroupStatus, CampaignGroupType, CampaignStatus, CampaignType } from '@/api/types/graphql';
import type {
  CampaignTemplatesQueryResponse,
  CampaignTemplatesQueryVariables,
} from '@/api/graphql/queries/campaignTemplates.generated';
import type {
  ShopPlatformQueryResponse,
  ShopPlatformQueryVariables,
} from '@/api/graphql/queries/shopPlatform.generated';

import { ref, watch } from 'vue';
import { CampaignTemplatesDocument } from '@/api/graphql/queries/campaignTemplates.generated';
import { ShopPlatformDocument } from '@/api/graphql/queries/shopPlatform.generated';
import { appFetcher } from '@/api/utils/gatewayClient';
import { app } from '@/store';
import { sentryCaptureException } from '@/exceptions/sentry';
import getParameterByName from '@/app/utils/getParameterByName';

export interface TemplateCampaign {
  id: string;
  name?: string;
  status?: CampaignStatus;
  type?: CampaignType;
  /** Set for a `TEMPLATE_BUNDLE` campaign: the multipage experiment it belongs to. */
  group?: {
    id: string;
    title?: string;
    status?: CampaignGroupStatus;
    type?: CampaignGroupType;
  };
}

/**
 * GemX state lives at module level instead of in a composable because the publish tooltip
 * (`liveView`) is built from jQuery code in gryffEditor, i.e. outside `setup()`. Neither vuex's
 * `useStore` nor vue-query hooks work there, so every caller just reads these refs.
 */
export const isConnectedGemX = ref(false);

/** The campaign that already A/B tests the template being edited, if any. */
export const templateCampaign = ref<TemplateCampaign | null>(null);

/**
 * A GemPages template is only targeted by these two campaign types in GemX: `TEMPLATE` for a single
 * page, `TEMPLATE_BUNDLE` for a page tested as part of a multipage experiment (a campaign group).
 */
const TEMPLATE_CAMPAIGN_TYPES: CampaignType[] = ['TEMPLATE', 'TEMPLATE_BUNDLE'];

/** Only the newest usable campaign is shown; a few are fetched so orphan bundles can be skipped. */
const MAX_CAMPAIGNS = 5;

let platformInflight: Promise<void> | null = null;
let campaignInflight: Promise<void> | null = null;

const getShopifyDomain = (): string => app.$store.state?.['app']?.['shopInfo']?.shopifyDomain || '';

/**
 * Fetches the GemX connection state once per session. Repeated calls are a no-op, except after a
 * failed attempt, which is allowed to be retried.
 */
export const fetchPlatformGemX = (): Promise<void> => {
  if (platformInflight) return platformInflight;

  const platformDomain = getShopifyDomain();
  if (!platformDomain) return Promise.resolve();

  platformInflight = appFetcher<ShopPlatformQueryResponse, ShopPlatformQueryVariables>(ShopPlatformDocument, {
    appType: 'GEMXP',
    platformDomain,
  })()
    .then((response) => {
      isConnectedGemX.value = response?.shopPlatform?.status === 'CONNECTED';
    })
    .catch((error) => {
      // A failure is indistinguishable from "the shop has not installed GemX" once it collapses
      // into `false`, so it has to be reported here — this is the only place that sees it.
      sentryCaptureException('fetchPlatformGemX', String(error), { platformDomain }, { level: 'error' });
      isConnectedGemX.value = false;
      platformInflight = null;
    });

  return platformInflight;
};

// shopInfo is loaded asynchronously while the editor boots, so wait for the domain before fetching.
watch(getShopifyDomain, (shopifyDomain) => shopifyDomain && fetchPlatformGemX(), { immediate: true });

/**
 * Whatever the resource type or file extension, a V6 template is named `gem-<pageId>-template`, so
 * that part alone is enough to match a variant's `templateSuffix`. The trailing `-template` keeps
 * `gem-12-template` from matching `gem-123-template`.
 */
const getTemplateSuffixKey = (): string => {
  const idPageTemplate = getParameterByName('id') || '';
  return idPageTemplate ? `gem-${idPageTemplate}-template` : '';
};

/**
 * Looks up the newest campaign covering the template being edited.
 *
 * Only the publish tooltip needs this, so it is fetched lazily instead of on boot: the publish flow
 * starts it and the tooltip reads the result. Call with `force` to refetch — a campaign can be
 * created from the tooltip link itself, in a GemX tab, between two publishes.
 */
export const fetchTemplateCampaign = (options?: { force?: boolean }): Promise<void> => {
  if (campaignInflight && !options?.force) return campaignInflight;

  // Campaigns only exist for a shop that connected GemX, and the query would be rejected anyway.
  if (!isConnectedGemX.value) return Promise.resolve();

  const templateSuffix = getTemplateSuffixKey();
  if (!templateSuffix) return Promise.resolve();

  campaignInflight = appFetcher<CampaignTemplatesQueryResponse, CampaignTemplatesQueryVariables>(
    CampaignTemplatesDocument,
    {
      first: MAX_CAMPAIGNS,
      orderBy: { direction: 'DESC', field: 'CREATED_AT' },
      where: {
        typeIn: TEMPLATE_CAMPAIGN_TYPES,
        hasVariantsWith: [{ templateSuffixContains: templateSuffix }],
        // Same guards as the V7 app's `useQueryCampaignsByThemePageId`: a deleted or stopped
        // campaign is over, so the page is free to be tested again.
        deletedAtIsNil: true,
        statusNotIn: ['STOPPED'],
      },
    },
  )()
    .then((response) => {
      const campaign = (response?.campaigns?.edges ?? [])
        .map((edge) => edge?.node)
        // A bundle campaign is only reachable through its group, so one without a group is skipped
        // (V7 filters it out the same way).
        .find((node) => !!node?.id && (node.type !== 'TEMPLATE_BUNDLE' || !!node.group?.id));
      const group = campaign?.group;

      templateCampaign.value = campaign?.id
        ? {
            id: campaign.id,
            name: campaign.name ?? '',
            status: campaign.status,
            type: campaign.type,
            group: group?.id
              ? { id: group.id, title: group.title ?? '', status: group.status, type: group.type }
              : undefined,
          }
        : null;
    })
    .catch((error) => {
      // Falling back to `null` shows the "create a campaign" tip, which is also what a shop with no
      // campaign sees — so a failure is invisible unless it is reported here.
      sentryCaptureException('fetchTemplateCampaign', String(error), { templateSuffix }, { level: 'error' });
      templateCampaign.value = null;
      campaignInflight = null;
    });

  return campaignInflight;
};

/**
 * Resolves once the pending lookup settles, or after `timeoutMs` — the publish tooltip is worth a
 * short wait to get the right variant, but never worth blocking on a slow network.
 *
 * Never rejects and always settles, so awaiting it cannot keep the tooltip from being rendered: a
 * failed lookup just leaves `templateCampaign` empty, which shows the "create a campaign" tip.
 */
export const waitForTemplateCampaign = (timeoutMs = 1500): Promise<void> => {
  if (!campaignInflight) return Promise.resolve();

  return Promise.race([
    campaignInflight,
    new Promise<void>((resolve) => {
      setTimeout(resolve, timeoutMs);
    }),
  ]).catch(() => undefined);
};
